import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listShared, type ViewerShare } from "../lib/api";

export function Shared() {
  const q = useQuery({ queryKey: ["shared"], queryFn: listShared, retry: false });

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 28px" }}>
      <div style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          Shared with you
        </h1>
        <p
          style={{
            margin: "8px 0 0",
            color: "var(--fg-3)",
            fontFamily: "var(--font-sans)",
            fontSize: 14,
          }}
        >
          Projects and widgets that other MCPBuild users have shared with this
          email. Open one to view it; connect its MCP endpoint to use it from
          Claude or ChatGPT.
        </p>
      </div>

      {q.isLoading && (
        <div style={{ color: "var(--fg-3)", fontFamily: "var(--font-mono)", fontSize: 13 }}>
          Loading…
        </div>
      )}
      {q.error && (
        <div style={{ color: "#F26D6D", fontFamily: "var(--font-mono)", fontSize: 13 }}>
          Could not load your shares. Try refreshing.
        </div>
      )}
      {q.data && q.data.shares.length === 0 && <EmptyState />}
      {q.data && q.data.shares.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {q.data.shares.map((s) => (
            <ShareCard key={s.shareId} share={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        marginTop: 24,
        padding: 28,
        border: "1px dashed var(--line-2)",
        borderRadius: 10,
        textAlign: "center",
        color: "var(--fg-3)",
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
      <p style={{ margin: 0 }}>
        No one has shared a project or widget with you yet.
      </p>
      <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--fg-4)" }}>
        When someone invites you, you'll see it here.
      </p>
    </div>
  );
}

function ShareCard({ share }: { share: ViewerShare }) {
  const label = share.toolSlug
    ? `${share.projectSlug} / ${share.toolSlug}`
    : share.projectSlug;
  const kind = share.toolSlug ? "Widget" : "Project";
  const to = `/projects/${share.projectSlug}?as=${encodeURIComponent(share.shareId)}`;
  const mcpUrl = `${window.location.origin}/mcp/shared/${share.shareId}`;
  return (
    <div
      style={{
        border: "1px solid var(--line-2)",
        background: "var(--surface-1)",
        borderRadius: 10,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--accent-fg)",
          }}
        >
          {kind}
        </span>
        <span
          title={`Permission: ${share.permission}`}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            padding: "1px 6px",
            border: "1px solid var(--line-2)",
            borderRadius: 999,
            color: share.permission === "write" ? "var(--accent-fg)" : "var(--fg-3)",
          }}
        >
          {share.permission}
        </span>
      </div>
      <div
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: 16,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          wordBreak: "break-word",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11.5,
          color: "var(--fg-4)",
          wordBreak: "break-all",
        }}
      >
        from {share.ownerEmail}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
        <Link
          to={to}
          style={{
            flex: 1,
            textAlign: "center",
            background: "var(--accent)",
            color: "#fff",
            padding: "8px 12px",
            borderRadius: 6,
            textDecoration: "none",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Open
        </Link>
        <button
          onClick={() => {
            navigator.clipboard.writeText(mcpUrl);
          }}
          title="Copy the MCP URL to connect from Claude / ChatGPT"
          style={{
            background: "var(--surface-2)",
            color: "var(--fg-1)",
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid var(--line-2)",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Copy MCP URL
        </button>
      </div>
    </div>
  );
}
